// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export function SetAlertButton({ userId, barcode, productName, price, retailer }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [targetPrice, setTargetPrice] = useState('');
  const [savedTargetPrice, setSavedTargetPrice] = useState(''); // Store for confirmation message
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Debug logging

  const handleOpenModal = () => {
    if (!userId) {
      alert('Please sign in to set price alerts');
      return;
    }
    
    // Validate required props
    if (!barcode) {
      alert('Product barcode is missing');
      return;
    }
    if (!productName) {
      alert('Product name is missing');
      return;
    }
    if (!price || isNaN(parseFloat(price))) {
      alert('Product price is missing or invalid');
      return;
    }
    
    setShowModal(true);
    setTargetPrice('');
    setError('');
  };

  const handleSetAlert = async () => {
    // Validate target price
    const target = parseFloat(targetPrice);
    const currentPrice = parseFloat(price);
    
    
    if (!targetPrice || isNaN(target) || target <= 0) {
      setError('Please enter a valid price');
      return;
    }

    if (target >= currentPrice) {
      setError(`Target price must be lower than current price (£${currentPrice.toFixed(2)})`);
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      
      const response = await axios.post(`${BACKEND_URL}/api/alerts`, {
        user_id: userId,
        barcode: barcode,
        product_name: productName,
        current_price: currentPrice,
        target_price: target,
        retailer: retailer
      });
      
      setSavedTargetPrice(target.toFixed(2)); // Save for confirmation message
      setSuccess(true);
      setShowModal(false);
      setShowConfirmation(true);
      
      // Hide confirmation after 5 seconds
      setTimeout(() => {
        setSuccess(false);
        setShowConfirmation(false);
      }, 5000);
    } catch (error) {
      console.error('[SET ALERT] Error:', error);
      console.error('[SET ALERT] Error response:', error.response?.data);
      
      if (error.response?.data?.message === 'Alert already exists') {
        setError('You already have an alert for this product at this retailer');
      } else if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to create alert. Please try again.');
      }
    }
    setLoading(false);
  };

  if (success) {
    return (
      <>
        <button
          type="button"
          disabled
          className="px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold text-lg shadow-lg flex items-center gap-2 w-full justify-center"
        >
          ✅ Alert Set Successfully!
        </button>
        
        {/* Confirmation Toast */}
        {showConfirmation && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-4 animate-fade-in">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🎉</span>
              <div className="flex-1">
                <p className="font-semibold text-green-800">Price Alert Created!</p>
                <p className="text-sm text-green-700 mt-1">
                  You'll receive a notification when <strong>{productName}</strong> drops to <strong>£{savedTargetPrice}</strong> at {retailer}
                </p>
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="mt-3 px-4 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition"
                >
                  Done ✓
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleOpenModal}
        className="px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-bold text-lg shadow-lg transition transform hover:scale-105 w-full flex items-center justify-center gap-2"
      >
        🔔 Set Price Alert
      </button>

      {/* Modal for setting target price */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-900">Set Price Alert</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-1">{productName}</p>
              <p className="text-xs text-gray-500">at {retailer}</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-800">Current Price:</span>
                <span className="text-lg font-bold text-blue-900">£{price.toFixed(2)}</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notify me when price drops to:
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">
                  £
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={price - 0.01}
                  value={targetPrice}
                  onChange={(e) => setTargetPrice(e.target.value)}
                  placeholder={(price * 0.9).toFixed(2)}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Suggested: £{(price * 0.9).toFixed(2)} (10% off) or £{(price * 0.8).toFixed(2)} (20% off)
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">⚠️ {error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSetAlert}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50"
              >
                {loading ? 'Setting...' : 'Set Alert'}
              </button>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600">
                💡 You'll be notified when the price at {retailer} drops to £{targetPrice || (price * 0.9).toFixed(2)} or lower
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function NotificationBell({ userId }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);

  React.useEffect(() => {
    if (userId) {
      loadUnreadNotifications();
      // Poll every 2 minutes (reduced from 30s to avoid excessive requests)
      const interval = setInterval(loadUnreadNotifications, 120000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  const loadUnreadNotifications = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications/${userId}?unread_only=true`);
      setUnreadCount(response.data.unread_count || 0);
      setNotifications(response.data.notifications || []);
    } catch (error) {
      // Silent fail — notifications are optional, don't spam console
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/${notificationId}/mark-read`);
      loadUnreadNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/${userId}/clear-all`);
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-900 transition"
        data-testid="notification-bell"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-gray-900">Notifications</h3>
              <div className="flex items-center gap-2">
                {notifications.length > 0 && (
                  <button
                    onClick={clearAllNotifications}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                    data-testid="clear-all-notifications"
                  >
                    Clear All
                  </button>
                )}
                <button
                  onClick={() => setShowDropdown(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">🔔</div>
                <p>No new notifications</p>
              </div>
            ) : (
              <div>
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className="p-4 border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => markAsRead(notification.id)}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="text-xl">💰</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">
                          {notification.title}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notification.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
